// Libs
import React from 'react';
import { withRouter } from 'react-router-dom';
import { Container, Form } from 'react-bootstrap';

// Services & Helpers
import GlobalStateService from 'services/GlobalStateService';
import InvoiceService from 'services/InvoiceService';
import BookingService from 'services/BookingService';
import LocationService from 'services/LocationService';
import BootboxHelper from 'helpers/BootboxHelper';
import TextHelpers from 'helpers/TextHelpers';

// Components
import Loader from 'components/common/Loader';
import BookingInvoiceModal from 'components/invoicing/BookingInvoiceModal';
import BookingsTable from 'components/bookings/BookingsTable';
import PageHeader from 'components/common/PageHeader';
import SideBar from 'components/common/SideBar';

//-------------------------------------------------------------------------------------------------------------------

class InvoicesPage extends React.Component {

    constructor(props) {
        super(props);

        this.bookingInvoiceModalRef = React.createRef();
        
        this.requestID = 1;

        this.state = {
            isLoading: false,
            isLoadingFilters: true,
            geos: [],
            filters: {
                vendorInvoiceStatus: 'NotInvoiced',
                geoID: null,
                includeNonATP: true,
                includeATP: true,
                vendorPaidMonth: null,
                goCoursesPaidMonth: null
            }
        };
    }

    async componentDidMount() {
        window.scrollTo(0, 0);
        GlobalStateService.setValues({ area: 'invoicing' });
        this.loadFilters();
    }

    async loadFilters() {
        this.setState({ isLoadingFilters: true });
        const filters = { ...this.state.filters };
        const geos = await LocationService.listGeos();

        // Select default geo
        const defaultGeo = geos.find(g => g.isDefault);
        if (defaultGeo) {
            filters.geoID = defaultGeo.id;
        }

        // Update UI
        this.setState({
            geos,
            filters,
            isLoadingFilters: false,
            isLoading: false
        });
    }

    componentDidUpdate(oldProps) {
        const oldParams = this.getParams(oldProps.match.params);
        const newParams = this.getParams(this.props.match.params);
        if (newParams.slug != oldParams.slug || newParams.tab != oldParams.tab) {
            this.load();
        }
    }
    
    getParams(params) {
        if (typeof (params) == 'undefined') {
            params = this.props.match.params;
        }
        let { slug } = params;
        if (slug == 'null') {
            slug = '';
        }
        return {
            slug: (slug || '')
        };
    }

    async load() {
        const { filters } = this.state;
        this.setState({ isLoading: true });
        this.requestID++;

        // Load bookings/invoices
        const newState = { isDirty: false, isLoaded: true, isLoading: false };
        newState.bookings = await BookingService.list({
            instructorID: null,
            isInvoicing: true,
            ...filters
        });

        // Load paid months
        if (filters.vendorInvoiceStatus == 'Paid') {
            newState.paidMonths = await BookingService.listPaidMonths({
                instructorID: null,
                isInvoicing: true,
                ...filters
            });
            newState.paidMonths.vendor = [{ value: '', text: '(Any date)' }].concat(newState.paidMonths.vendor);
            newState.paidMonths.goCourses = [{ value: '', text: '(Any)' }].concat(newState.paidMonths.goCourses);
        }   
            
        newState.isLoading = false;
        this.setState(newState);
    }

    updateFilter(name, value) {
        const filters = { ...this.state.filters };
        if (filters[name] != value) {
            filters[name] = value;

            this.setState({
                filters,
                isDirty: true
            });
        }
    }

    updateInvoiceRefSelect(value) {
        value = value || '';
        if (value.indexOf('\n') != -1) {
            value += '\n';
        }
        const regEx = new RegExp(/([a-z0-9]+)([^a-z0-9])/, 'mig');
        const matches = value.matchAll(regEx);
        const references = [];
        if (matches) {
            for (const match of matches) {
                references.push(match[1].toLowerCase());
            }
            if (references.length > 0) {
                this.trySelectInvoices(references);
                value = '';
            }
        }
        this.setState({
            invoiceRefSelect: value
        });
    }

    trySelectInvoices(references) {
        const bookings = [...this.state.bookings];
        const refsNotFound = [];
        let numFound = 0;
        
        references.forEach(reference => {
            let isFound = false;
            bookings.forEach(booking => {
                if ((booking.vendorInvoiceReference || '').toLowerCase() == reference ||
                    (booking.lmsid || '').toLowerCase() == reference
                ) {
                    isFound = true;
                    if (!booking.hasVendorPaid) {
                        booking.isSelected = true;
                        numFound++;
                    }
                }
            });
            if (!isFound) {
                refsNotFound.push(reference);
            }
        });
        if (numFound > 0) {
            this.requestID++;
            this.setState({
                bookings,
                numSelected: this.countNumSelected(bookings)
            });
        }
        if (refsNotFound.length > 0) {
            BootboxHelper.alert(
                'The following references could not be found: <ul>' +
                refsNotFound.map(r => `<li>${r}</li>`).join('\r\n') +
                '</ul>'
            );
        }
    }

    setBookingIsSelected(id, isSelected) {
        const bookings = [...this.state.bookings];
        const index = bookings.findIndex(b => b.id == id);
        bookings[index].isSelected = isSelected;
        this.requestID++;
        this.setState({
            bookings,
            numSelected: this.countNumSelected(bookings)
        });
    }

    countNumSelected(bookings) {
        let numSelected = 0;
        bookings.forEach(b => {
            if (b.isSelected) {
                numSelected++;
            }
        });
        return numSelected;
    }

    async confirmMarkPaidByVendor() {
        const references = [];
        this.state.bookings.forEach(b => {
            if (b.isSelected) {
                references.push(b.vendorInvoiceReference);
            }
        });
        const confirm = await BootboxHelper.confirm(
            'Mark the following invoices as <b>paid by vendor</b>?<ul>' +
            references.map(r => `<li>${TextHelpers.escapeHTML(r)}</li>`).join('\n') +
            '</ul>'
        );
        if (confirm) {
            this.markPaidByVendor();
        }
    }

    async markPaidByVendor() {
        const bookingIDs = [];
        this.state.bookings.forEach(b => {
            if (b.isSelected) {
                bookingIDs.push(b.id);
            }
        });
        this.setState({ isLoading: true });
        await InvoiceService.markPaidByVendor(bookingIDs);
        this.load();
        BootboxHelper.alert('Invoice(s) marked as paid by vendor');
    }

    render() {
        const {
            numSelected
        } = this.state;

        return (<>

            <div className="page invoices-page full-width-page">

                <PageHeader hasSideBar>
                    <h1>Invoices</h1>

                    <div className="buttons">

                        {numSelected > 0 &&
                            <button
                                className="btn btn-primary"
                                onClick={() => this.confirmMarkPaidByVendor()}
                            >
                                Mark as paid by vendor ({numSelected})
                            </button>
                        }

                    </div>
                </PageHeader>

                <main>

                    <SideBar content={this.renderSideBarContent()} />

                    <Container fluid>

                        <section>

                            {this.renderInner()}

                        </section>

                    </Container>

                </main>

            </div>

            <BookingInvoiceModal ref={this.bookingInvoiceModalRef} />

        </>);
    }

    renderInner() {
        const {
            isLoading,
            isLoaded,
            bookings,
            filters
        } = this.state;

        if (isLoading) {
            return (<Loader fullPage />);
        }
        if (!isLoaded) {
            return (
                <div className="empty-text">
                    Please press 'Load invoices'.
                </div>
            );
        }

        const loginInfo = GlobalStateService.getValue('loginInfo');

        return (
            <BookingsTable
                mode="invoices"
                className="admin-mode"
                vendorInvoiceStatus={filters.vendorInvoiceStatus}
                isATP
                requestID={this.requestID}
                bookings={bookings}
                loginInfo={loginInfo}
                isEditMode
                isScrolling
                toggleSelected={(id, isSelected) => this.setBookingIsSelected(id, isSelected)}
                getIsSelectable={booking => !booking.hasVendorPaid && booking.vendorInvoiceReference}
                onClick={async (booking) => {
                    await this.bookingInvoiceModalRef.current.show({
                        bookingID: booking.id
                    });
                    this.load();
                }}
            />
        );
    }

    renderSideBarContent() {
        const { filters, geos, paidMonths, invoiceRefSelect, isLoading, isDirty } = this.state;

        return (

            <form onSubmit={e => {
                e.preventDefault();
                this.load();
            }}>

                {filters.vendorInvoiceStatus == 'NotPaid' &&
                    <Form.Group className="mb-3">
                        <Form.Label>Select invoices</Form.Label>
                        <textarea
                            className="form-control invoice-ref-select"
                            value={invoiceRefSelect}
                            onChange={e => this.updateInvoiceRefSelect(e.target.value)}
                            onBlur={e => {
                                if (e.target.value) {
                                    this.updateInvoiceRefSelect(e.target.value + '\n');
                                }
                            }}
                            rows={1}
                        />
                        <div className="alert alert-info form-group-info">
                            Type or copy & paste Inv No. / {filters.geoID == 'atp' ? 'booking references' : 'LMS IDs'} to select them
                        </div>
                    </Form.Group>
                }
            
                <Form.Group className="mb-3">
                    <Form.Label>Invoice status</Form.Label>
                    <select
                        className="form-control"
                        value={filters.vendorInvoiceStatus}
                        onChange={e => this.updateFilter('vendorInvoiceStatus', e.target.value)}
                    >
                        <option value="">(Any status)</option>
                        <option value="NotInvoiced">Ready to invoice</option>
                        <option value="NotPaid">Invoiced</option>
                        <option value="Paid">Paid</option>
                    </select>
                </Form.Group>

                {filters.vendorInvoiceStatus == 'Paid' && paidMonths &&
                    <Form.Group className="mb-3">
                        <Form.Label>Paid in</Form.Label>
                        <select
                            className="form-control"
                            value={filters.dateVendorPaid}
                            onChange={e => this.updateFilter('dateVendorPaid', e.target.value)}
                        >
                            {paidMonths.vendor.map(month =>
                                <option key={month.value} value={month.value}>
                                    {month.text}
                                </option>
                            )}
                        </select>
                    </Form.Group>
                }

                <Form.Group className="mb-3">
                    <Form.Label>Geo</Form.Label>
                    <select
                        className="form-control"
                        value={filters.geoID || ''}
                        onChange={e => this.updateFilter('geoID', e.target.value)}
                    >
                        {geos.map(g =>
                            <option key={g.id} value={g.id}>
                                {g.name}
                            </option>
                        )}
                    </select>
                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Label>Other options</Form.Label>
                    <Form.Check
                        type="checkbox"
                        id="include-non-atp"
                        checked={!!filters.includeNonATP}
                        onChange={e => this.updateFilter('includeNonATP', e.target.checked)}
                        label="Include non-ATP bookings"
                    />
                    <Form.Check
                        type="checkbox"
                        id="include-atp"
                        checked={!!filters.includeATP}
                        onChange={e => this.updateFilter('includeATP', e.target.checked)}
                        label="Include ATP bookings"
                    />
                </Form.Group>

                <button type="submit" className="btn btn-primary w-100 mt-auto" disabled={isLoading}>
                    Load invoices {isDirty ? ' *' : ''}
                </button>

            </form>
        );
    }
}

export default withRouter(InvoicesPage);